import React, { memo, useCallback, useEffect, useState } from "react";

import { useMutation } from "@apollo/client";
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  InternalAdminTagFragment,
  InternalAdminTagsQuery,
  InternalAdminTagsQueryVariables,
  InternalAdminTagsSetActiveMutation,
  InternalAdminTagsSetActiveMutationVariables,
} from "gql/graphql";
import Toast from "modules/toasts/toast";
import { getTagMention } from "modules/utils/Functions";
import colors from "app/colors";
import useBurstQuery from "modules/hooks/use-burst-query";
import { RestoreIcon, GreenCheckIcon } from "modules/icons";
import INTERNAL_ADMIN_TAGS_QUERY from "../_networking/internal-admin-tags";
import INTERNAL_ADMIN_TAGS_SET_ACTIVE_MUTATION from "../_networking/internal-admin-tags-set-active";
import useQueryString from "modules/hooks/use-query-string";
import useDisplayToast from "modules/hooks/use-display-toast";
import { useOverlay } from "modules/providers/overlay-provider";

interface IProps {
  tagIds: string[];
}

const InternalAdminConfirmUnarchiveTagModal = ({ tagIds }: IProps) => {
  const { closeModal } = useOverlay();
  const { qsOrganizationId } = useQueryString();
  const { displayToast } = useDisplayToast();
  const [tags, setTags] = useState<ReadonlyArray<InternalAdminTagFragment>>([]);
  const [internalAdminTagsSetActiveApi] = useMutation<
    InternalAdminTagsSetActiveMutation,
    InternalAdminTagsSetActiveMutationVariables
  >(INTERNAL_ADMIN_TAGS_SET_ACTIVE_MUTATION);

  const [internalAdminTagsAPI] = useBurstQuery<
    InternalAdminTagsQuery,
    InternalAdminTagsQueryVariables
  >(INTERNAL_ADMIN_TAGS_QUERY);

  const getTags = useCallback(async () => {
    const response = await internalAdminTagsAPI({
      variables: { id: tagIds },
    });
    if (response.data) {
      setTags(response.data.internalAdminTag);
    }
  }, [tagIds, internalAdminTagsAPI]);

  useEffect(() => {
    getTags();
  }, [getTags]);

  const tagsFirstThree = tags.slice(0, 3);

  const onUnarchiveClicked = useCallback(async () => {
    if (!qsOrganizationId) return;
    await internalAdminTagsSetActiveApi({
      variables: {
        organizationId: qsOrganizationId,
        active: true,
        idList: tagIds,
      },
    });
    displayToast(() => (
      <Toast
        message={`${tagIds.length} Tags Unarchived`}
        leftIcon={<RestoreIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
    closeModal();
  }, [
    internalAdminTagsSetActiveApi,
    qsOrganizationId,
    tagIds,
    displayToast,
    closeModal,
  ]);

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader pt={8}>
          <VStack spacing={6}>
            <GreenCheckIcon />
            <Text fontSize={26}>Unarchive Tag{tags.length > 1 && "s"}?</Text>
          </VStack>
        </ModalHeader>
        <ModalBody pb={6}>
          {tags.length === 0 ? (
            <Spinner size="xl" />
          ) : (
            <VStack spacing={6}>
              <Text fontSize={16} color={colors.gray50} textAlign="center">
                Are you sure you want to unarchive:
                <Text>
                  {tagsFirstThree.map((tag, index) => (
                    <React.Fragment key={tag.id}>
                      {index < 3 && index > 0 && ", "}
                      {getTagMention(tag)}
                    </React.Fragment>
                  ))}
                  {tags.length > 3 ? `, and ${tags.length - 3} more?` : "?"}
                </Text>
              </Text>
              <HStack>
                <Button variant="cancel" height={8} w={28} onClick={closeModal}>
                  No
                </Button>
                <Button height={8} w={28} onClick={onUnarchiveClicked}>
                  Unarchive
                </Button>
              </HStack>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminConfirmUnarchiveTagModal);
